import { ApiError } from "@/api/client";

/**
 * When a failed read is worth asking again — the app's one retry policy.
 *
 * Extracted from `main.tsx`'s inline `defaultOptions.queries.retry`
 * (flowstack#994 item 3) so a fan-out that DEPENDS on it can name it instead
 * of inheriting it silently from a provider three files away. The policy
 * itself is unchanged; there is still exactly one implementation.
 *
 * **A 4xx is an answer, not a transient.** "You may not see this run" and
 * "there is no such run" are decisions the server already made; asking twice
 * more cannot change either, and the cost is paid per run in the cross-run
 * overlay's fan-out — so a link naming runs the viewer cannot access would
 * cost strictly more than one naming runs they can, and would sit on the
 * "could not be found" line for seconds while the chart renders short. That is
 * the guarantee `AnalysisComparePage` makes, inverted.
 *
 * Everything else — 5xx, a dropped connection (`TypeError` from `fetch`), a
 * malformed body — may genuinely differ on the next attempt, so it gets
 * {@link MAX_QUERY_RETRIES} more.
 */
export const MAX_QUERY_RETRIES = 2;

export function retryQuery(failureCount: number, error: unknown): boolean {
  // `instanceof ApiError` is the same identity check `classifyApiError` makes
  // (`@/lib/api-error-kind`), so the two cannot disagree about what an API
  // error is — which they could when this tested `error.name` instead.
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < MAX_QUERY_RETRIES;
}
